import { ComponentInteractionType, SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { Inputs, Outputs } from '@/_models/virtualStaging/Tube/component/types';

export class TubeComponent extends SceneComponent {
  public root: any = null;

  public pivot: any = null;

  public isDestroyed = false;

  public arrowMaterial = null;

  public innerTubeMaterial = null;

  public renderer = null;

  inputs: Inputs = {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    visible: true,
    opacity: 0.35,
    color: 'red',
    hasTexture: true,
    textureRepeat: { x: 7, y: 4 },
    textureOffset: { x: 0, y: 0 },
    length: 10,
    width: 1,
    scale: 1,
    arrowSpeed: 0.004,
    linkedPolygon: null,
    interval: 0,
    delay: 0,
  };

  outputs: Outputs = {
    loadingState: LoadingState.Idle,
  };

  events = {
    [ComponentInteractionType.CLICK]: false,
    [ComponentInteractionType.HOVER]: false,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.root.position.set(this.inputs.position.x, this.inputs.position.y, this.inputs.position.z);
    this.root.visible = this.inputs.visible;
    this.root.scale.set(this.inputs.scale, this.inputs.scale, this.inputs.scale);
    this.pivot = new THREE.Object3D();
    this.root.add(this.pivot);
    this.pivot.rotation.set(this.inputs.rotation.x, this.inputs.rotation.y, this.inputs.rotation.z);
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;
    this.createPipe();
    this.outputs.loadingState = LoadingState.Loading;
  }

  async createPipe() {
    const THREE = this.context.three;
    const path = new THREE.CatmullRomCurve3([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, this.inputs.length)]);

    const tubeGeometry = new THREE.TubeGeometry(path, 1, this.inputs.width, 24, false);

    this.innerTubeMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: this.inputs.opacity,
      color: this.inputs.color,
    });

    if (this.inputs.hasTexture) {
      const textureLoader = new THREE.TextureLoader();
      const arrowTexture = await textureLoader.load('/images/Arrow-Right.png');
      arrowTexture.wrapS = THREE.RepeatWrapping;
      arrowTexture.wrapT = THREE.RepeatWrapping;
      arrowTexture.repeat.set(this.inputs.textureRepeat.x, this.inputs.textureRepeat.y);
      arrowTexture.offset.set(this.inputs.textureOffset.x, this.inputs.textureOffset.y);

      this.arrowMaterial = new THREE.MeshBasicMaterial({
        map: arrowTexture,
        transparent: true,
        opacity: 1,
        depthWrite: false,
        side: THREE.FrontSide,
      });

      const outerTubeMesh = new THREE.Mesh(tubeGeometry, this.arrowMaterial);
      outerTubeMesh.scale.set(1.05, 1.05, 1);
      outerTubeMesh.renderOrder += 1;
      this.pivot.add(outerTubeMesh);
    }

    const innerTubeMesh = new THREE.Mesh(tubeGeometry, this.innerTubeMaterial);
    this.pivot.add(innerTubeMesh);
  }

  setVisible(visible) {
    this.inputs.visible = visible;
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    this.root.visible = this.inputs.visible;
    if (previousInputs.color !== this.inputs.color && this.innerTubeMaterial) {
      this.innerTubeMaterial.color.set(this.inputs.color);
      this.innerTubeMaterial.needsUpdate = true;
    }
  }

  onTick() {
    if (this.arrowMaterial && !this.renderer?.xr?.isPresenting) {
      const { arrowSpeed } = this.inputs;

      const arrowOffset = this.arrowMaterial.map.offset;
      arrowOffset.x -= arrowSpeed;
      this.arrowMaterial.map.offset = arrowOffset;
    }
  }

  onDestroy() {}
}

export const tubeComponentType = 'mp.tubeComponent';
export const makeTubeComponent = (): any => new TubeComponent();
